/**
 * The company card itself — sv_organization, addressed by id in the PATH.
 *
 * `GET /companies` and the two creation routes already live in
 * `entities/session/api.ts`, because the session resolver needs them before any
 * company context exists. This file is the rest: reading one company's card and
 * editing or deleting it, which only makes sense once a context is chosen.
 *
 * `request()`, not `requestControl()`: the Casbin domain for this family comes from
 * the id in the path, not from `x-company-id` (conventions.md §3).
 */
import {request} from '@/shared/api';
import type {Company} from '@/entities/session/contexts';

/**
 * The full card. Wider than the `Company` the list returns — it carries the contact
 * block and an owner summary — but every extra field is optional here so a screen
 * built against it still compiles against a leaner deployment.
 */
export type CompanyCard = Company & {
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  logo_url?: string | null;
  status?: string | null;
  created_at?: string | null;
  owner?: {id: string; full_name?: string; email?: string} | null;
};

export function fetchCompany(companyId: string): Promise<CompanyCard> {
  return request<CompanyCard>(`/companies/${encodeURIComponent(companyId)}`);
}

/**
 * Edit the card. Every field is optional and omitted ones are left alone.
 *
 * Two fields are deliberately NOT exposed: `notes` (operator-only — a write from
 * outside the system domain is discarded in silence, so offering the field would show
 * an edit that never lands) and `owner_account_id` (transfer is platform-managed; a
 * change from a company domain is a 403). Neither belongs in a company owner's UI.
 */
export function updateCompany(
  companyId: string,
  update: {
    name?: string;
    organization_type?: string;
    address?: string;
    email?: string;
    /**
     * Accepted by the gateway and listed alongside `name`/`address` in
     * conventions.md §9, but missing here until 2026-08-15 — so the card could show a
     * phone it had no way to set. A field that can be read and not written is how a
     * screen ends up with a value nobody can correct.
     */
    phone?: string;
    logo_url?: string;
  },
): Promise<CompanyCard> {
  return request<CompanyCard>(`/companies/${encodeURIComponent(companyId)}`, {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

/**
 * Delete the company. Soft by default — `hard: true` is the irreversible purge, and is
 * not offered anywhere in the cabinet's UI; it exists here only so the parameter is
 * documented rather than discovered.
 */
export function deleteCompany(
  companyId: string,
  options: {hard?: boolean} = {},
): Promise<void> {
  const query = options.hard ? '?hard=true' : '';
  return request<void>(`/companies/${encodeURIComponent(companyId)}${query}`, {
    method: 'DELETE',
  });
}
